import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import {
  CANONICAL_FIELDS,
  parseCsvText,
  buildInitialMapping,
  rowToGoalDraft,
  validateDraft,
  findLikelyDuplicates,
  buildCsvTemplate,
} from '../../lib/iepGoalsCsv';

// ---------------------------------------------------------------------------
// Import IEP goals (CSV) — /goals/import
//
// A four-step wizard that lets a teacher bulk-load a caseload's goals
// from a spreadsheet exported by their IEP-management platform (EdPlan,
// SEIS, Frontline, IEP Direct, Enrich, SchoolMint, Goalbook, etc.).
// Sibling to /students/import — one adds people, this one adds goals.
//
// Steps:
//
//   1. Upload — file picker + drag/drop, plus "Download template" so the
//      teacher can hand it to their district's data coordinator.
//
//   2. Map columns — auto-suggests mappings for each CSV header (Goal
//      Text, PLAAFP, Measurement Method, etc.); teacher can override any.
//      First 3 preview rows update live.
//
//   3. Preview + validate — every row is normalized into a goal draft,
//      validated (goal text / measure / target required, student
//      resolved to a real students.id), and duplicate-checked against
//      the existing goals list. Bad rows are called out inline; the
//      teacher picks which to skip.
//
//   4. Import — creates goals one at a time via addGoal() so we inherit
//      the usual Supabase / demo-mode paths. Shows a per-row progress
//      indicator.
//
// After success we route back to /iep-goals-and-objectives-tracking so
// the newly-imported goals are right in front of her.
// ---------------------------------------------------------------------------

const STEPS = [
  { key: 'upload',  label: '1. Upload' },
  { key: 'map',     label: '2. Map columns' },
  { key: 'preview', label: '3. Preview' },
  { key: 'import',  label: '4. Import' },
];

function downloadFile(filename, content, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const GoalsImportPage = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { students } = useStudents();
  const { goals, addGoal, isLive } = useIEPGoals();

  const [step, setStep] = useState('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [mapping, setMapping] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  // Per-row decisions the teacher makes on the Preview screen.
  // rowIndex -> 'import' | 'skip'.
  const [rowDecisions, setRowDecisions] = useState({});

  const [importLog, setImportLog] = useState([]); // [{rowIndex, name, status, error}]
  const [isImporting, setIsImporting] = useState(false);

  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setParseError(null);
    const reader = new FileReader();
    reader.onerror = () => {
      setParseError('Couldn\'t read that file — is it definitely a .csv?');
    };
    reader.onload = () => {
      const text = reader.result;
      if (typeof text !== 'string') {
        setParseError('Couldn\'t read that file — is it definitely a .csv?');
        return;
      }
      const parsed = parseCsvText(text);
      if (parsed.rows.length === 0) {
        setParseError('The file didn\'t contain any goal rows.');
        return;
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(buildInitialMapping(parsed.headers));
      setRowDecisions({});
      setStep('map');
    };
    reader.readAsText(file);
  }, []);

  const onFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // studentId -> "First Last" for rendering the Preview screen with a
  // human name instead of a UUID.
  const studentNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(students) ? students : []).forEach((s) => {
      const name =
        `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student';
      map.set(s.id, name);
    });
    return map;
  }, [students]);

  const drafts = useMemo(() => {
    return rows.map((row) => {
      const { draft, studentMatch } = rowToGoalDraft(row, mapping, students);
      const validation = validateDraft(draft, studentMatch);
      const duplicates = findLikelyDuplicates(draft, goals);
      return {
        rowIndex: row.__rowIndex,
        draft,
        studentMatch,
        validation,
        duplicates,
      };
    });
  }, [rows, mapping, students, goals]);

  const previewRows = useMemo(() => drafts.slice(0, 3), [drafts]);

  const importCounts = useMemo(() => {
    let good = 0;
    let badRequired = 0;
    let likelyDup = 0;
    let warned = 0;
    for (const d of drafts) {
      if (!d.validation.valid) badRequired += 1;
      else if (d.duplicates.length > 0) likelyDup += 1;
      else if (d.validation.warnings.length > 0) warned += 1;
      else good += 1;
    }
    return { good, badRequired, likelyDup, warned, total: drafts.length };
  }, [drafts]);

  // For each row: errors -> skip, teacher explicit skip -> skip,
  // otherwise import.
  const decidedRows = useMemo(() => {
    return drafts.map((d) => {
      let action = 'import';
      if (!d.validation.valid) action = 'skip';
      else if (rowDecisions[d.rowIndex] === 'skip') action = 'skip';
      return { ...d, action };
    });
  }, [drafts, rowDecisions]);

  const toImportCount = decidedRows.filter((r) => r.action === 'import').length;

  // Advance-to-preview gate: teacher has to have mapped either a
  // studentName column OR both studentFirstName + studentLastName, plus
  // the three required goal fields.
  const canGoToPreview = useMemo(() => {
    const mappedFields = new Set(
      Object.values(mapping).filter((v) => v && v !== '__ignore__'),
    );
    const hasStudentName =
      mappedFields.has('studentName') ||
      (mappedFields.has('studentFirstName') && mappedFields.has('studentLastName'));
    return (
      hasStudentName &&
      mappedFields.has('specific') &&
      mappedFields.has('measurable') &&
      mappedFields.has('target')
    );
  }, [mapping]);

  const handleRunImport = useCallback(async () => {
    if (isImporting) return;
    setIsImporting(true);
    setStep('import');
    const importable = decidedRows.filter((r) => r.action === 'import');
    const log = [];
    for (const item of importable) {
      const label =
        studentNameById.get(item.draft.studentId) ||
        item.studentMatch?.rawName ||
        'Goal';
      const payload = {
        studentId: item.draft.studentId,
        // behavior_goals requires goal_area not null. If the teacher
        // didn't map a column we default to "other" — flagged as a
        // warning on the preview screen so this isn't invisible.
        goalArea: item.draft.goalArea || 'other',
        specific: item.draft.specific,
        measurable: item.draft.measurable,
        baseline: item.draft.baseline || null,
        target: item.draft.target,
        masteryCriteria: item.draft.masteryCriteria || null,
        targetDate: item.draft.targetDate || null,
        targetValue: item.draft.targetValue == null ? null : Number(item.draft.targetValue),
        unit: item.draft.unit || null,
        status: item.draft.status || 'in_progress',
      };
      try {
        await addGoal(payload);
        log.push({
          rowIndex: item.rowIndex,
          name: label,
          status: 'added',
        });
      } catch (err) {
        log.push({
          rowIndex: item.rowIndex,
          name: label,
          status: 'error',
          error: err?.message || 'Unknown error',
        });
      }
      setImportLog([...log]);
    }
    setIsImporting(false);
  }, [addGoal, decidedRows, isImporting, studentNameById]);

  const successCount = importLog.filter((r) => r.status === 'added').length;
  const errorCount = importLog.filter((r) => r.status === 'error').length;

  const handleStartOver = () => {
    setStep('upload');
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setRowDecisions({});
    setImportLog([]);
    setParseError(null);
  };

  const hasNoRoster = !Array.isArray(students) || students.length === 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="p-4 sm:p-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <Breadcrumb
              customItems={[
                { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                { label: 'IEP Goals', path: '/iep-goals-and-objectives-tracking', icon: 'Target' },
                { label: 'Import from spreadsheet', path: null, icon: 'Upload', current: true },
              ]}
            />

            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Import IEP goals from a spreadsheet
              </h1>
              <p className="text-sm text-muted-foreground max-w-3xl mt-1">
                Upload a CSV exported from your IEP platform (EdPlan,
                SEIS, Frontline, IEP Direct, Enrich, SchoolMint, Goalbook —
                anything that exports a spreadsheet). The app will match
                your columns to the goal fields, resolve each student
                against your roster, flag anything missing, and add every
                goal in one step.
              </p>
              {!isLive && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 max-w-3xl">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                    <p>
                      You're in demo mode. Importing here will fill your
                      demo goals on this browser only — nothing is saved
                      to a real database.
                    </p>
                  </div>
                </div>
              )}
              {hasNoRoster && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 max-w-3xl">
                  <div className="flex items-start gap-2">
                    <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">
                        You don't have any students on your roster yet.
                      </p>
                      <p className="mt-1">
                        Every goal needs to be tied to a student. Add
                        students first (either{' '}
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => navigate('/student-profile-management')}
                        >
                          one at a time
                        </button>
                        {' '}or{' '}
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => navigate('/students/import')}
                        >
                          from a spreadsheet
                        </button>
                        ), then come back here to import their goals.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Step strip */}
            <div className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="flex overflow-x-auto">
                {STEPS.map((s) => {
                  const isActive = s.key === step;
                  return (
                    <div
                      key={s.key}
                      className={`flex-1 min-w-[140px] px-4 py-3 border-b-2 text-sm font-medium ${
                        isActive
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-transparent text-muted-foreground'
                      }`}
                    >
                      {s.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* STEP: UPLOAD -------------------------------------------- */}
            {step === 'upload' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div
                  onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  onDrop={onDrop}
                  className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary bg-primary/5'
                      : 'border-border bg-background'
                  }`}
                >
                  <Icon name="Upload" size={32} className="mx-auto text-primary mb-3" />
                  <p className="text-base font-medium text-foreground">
                    Drop a .csv file here, or pick one from your computer
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Excel, Google Sheets, Numbers — any of them can export CSV.
                  </p>
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv,text/plain"
                      onChange={onFileInputChange}
                      className="hidden"
                    />
                    <Button
                      variant="default"
                      onClick={() => fileInputRef.current?.click()}
                      className="min-h-12"
                    >
                      <Icon name="FolderOpen" size={16} />
                      Pick a CSV file
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => downloadFile('edutracker-iep-goals-template.csv', buildCsvTemplate())}
                      className="min-h-12"
                      title="Download a starter CSV to fill in"
                    >
                      <Icon name="Download" size={16} />
                      Download template
                    </Button>
                  </div>
                </div>

                {parseError && (
                  <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-foreground">
                          Couldn't read the file
                        </p>
                        <p className="text-muted-foreground mt-1">{parseError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">
                        What should be in the CSV?
                      </p>
                      <p className="mt-1">
                        At minimum, four columns: a way to identify the
                        student (either "Student" as one column or "First
                        Name" + "Last Name" as two), the goal statement,
                        how progress will be measured, and the target
                        level. Everything else — baseline, mastery
                        criteria, target date, status — is optional. If
                        your IEP platform uses different column names
                        ("Goal Text", "PLAAFP", "Measurement Method"),
                        we'll try to auto-match them and let you override
                        on the next screen.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* STEP: MAP ---------------------------------------------- */}
            {step === 'map' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      Match up the columns
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-2xl">
                      For each column from your spreadsheet, tell the app
                      which goal field it should go into. Student, goal
                      text, measurement, and target are required.
                      Everything else can be left as "Ignore."
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    <Icon name="File" size={12} className="inline mr-1" />
                    {fileName || 'goals.csv'} — {rows.length} rows
                  </span>
                </div>

                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2 w-1/3">Column from your file</th>
                        <th className="text-left px-3 py-2 w-1/3">Maps to</th>
                        <th className="text-left px-3 py-2">Preview (first row)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headers.map((h) => {
                        const currentValue = mapping[h] || '__ignore__';
                        const firstPreview = rows[0]?.[h] || '';
                        return (
                          <tr key={h} className="border-t border-border">
                            <td className="px-3 py-2 font-medium text-card-foreground">
                              {h || <span className="italic text-muted-foreground">(blank header)</span>}
                            </td>
                            <td className="px-3 py-2">
                              <Select
                                options={CANONICAL_FIELDS.map((f) => ({
                                  value: f.key,
                                  label: f.label + (f.required ? ' *' : ''),
                                }))}
                                value={currentValue}
                                onChange={(v) => setMapping((prev) => ({ ...prev, [h]: v }))}
                              />
                            </td>
                            <td className="px-3 py-2 text-muted-foreground truncate max-w-xs">
                              {firstPreview || <span className="italic">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!canGoToPreview && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
                      <p>
                        You need to map a way to identify the student
                        (either <strong>Student — full name</strong> or
                        both <strong>Student — first name</strong> and
                        <strong> Student — last name</strong>), plus
                        <strong> Goal text</strong>,
                        <strong> How progress is measured</strong>, and
                        <strong> Target level</strong>.
                      </p>
                    </div>
                  </div>
                )}

                <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <Icon name="Eye" size={14} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Live preview</p>
                      <p className="mt-1">
                        Here's how the first {previewRows.length} rows will look
                        after mapping:
                      </p>
                      <ul className="mt-2 space-y-1">
                        {previewRows.map((p) => {
                          const student =
                            studentNameById.get(p.draft.studentId) ||
                            p.studentMatch?.rawName ||
                            '—';
                          const goalPreview = (p.draft.specific || '—').slice(0, 80);
                          return (
                            <li key={p.rowIndex} className="text-sm">
                              <span className="font-medium text-foreground">
                                {student}
                              </span>{' '}
                              <span className="text-muted-foreground">
                                {p.draft.goalArea && `· ${p.draft.goalArea}`}
                                {goalPreview && ` · ${goalPreview}${p.draft.specific?.length > 80 ? '…' : ''}`}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep('upload')}>
                    <Icon name="ArrowLeft" size={14} />
                    Back
                  </Button>
                  <Button
                    variant="default"
                    onClick={() => setStep('preview')}
                    disabled={!canGoToPreview}
                    className="min-h-12"
                  >
                    Continue
                    <Icon name="ArrowRight" size={14} />
                  </Button>
                </div>
              </section>
            )}

            {/* STEP: PREVIEW ----------------------------------------- */}
            {step === 'preview' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    Review before you import
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {toImportCount} of {importCounts.total} rows will be added.
                    Rows whose student can't be matched or that are missing
                    required goal fields are always skipped. Possible
                    duplicates are pre-flagged — you decide whether to add
                    them anyway.
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <SummaryTile label="Ready to add" count={importCounts.good} tone="ok" />
                  <SummaryTile label="Likely duplicates" count={importCounts.likelyDup} tone="warn" />
                  <SummaryTile label="Rows with warnings" count={importCounts.warned} tone="warn" />
                  <SummaryTile label="Blocked (skip)" count={importCounts.badRequired} tone="bad" />
                </div>

                <div className="rounded-md border border-border overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Student</th>
                        <th className="text-left px-3 py-2">Area · goal · target</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-left px-3 py-2">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decidedRows.map((r) => {
                        const isSkipped = r.action === 'skip';
                        const studentLabel =
                          studentNameById.get(r.draft.studentId) ||
                          (r.studentMatch?.rawName
                            ? `${r.studentMatch.rawName} — no match`
                            : 'No student');
                        const goalPreview = (r.draft.specific || '')
                          .slice(0, 100);
                        return (
                          <tr key={r.rowIndex} className={`border-t border-border ${isSkipped ? 'opacity-60' : ''}`}>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              #{r.rowIndex}
                            </td>
                            <td className="px-3 py-2 font-medium text-card-foreground">
                              {studentLabel}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {[
                                r.draft.goalArea || '(no area)',
                                goalPreview || '(no goal text)',
                                r.draft.target ? `→ ${r.draft.target}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </td>
                            <td className="px-3 py-2">
                              {r.validation.errors.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                  <Icon name="AlertCircle" size={12} />
                                  {r.validation.errors[0]}
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                                  <Icon name="AlertTriangle" size={12} />
                                  Looks like a duplicate goal
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length === 0 &&
                                r.validation.warnings.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                                  <Icon name="AlertTriangle" size={12} />
                                  {r.validation.warnings[0]}
                                </span>
                              )}
                              {r.validation.errors.length === 0 &&
                                r.duplicates.length === 0 &&
                                r.validation.warnings.length === 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                  <Icon name="Check" size={12} />
                                  Ready
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {r.validation.errors.length > 0 ? (
                                <span className="text-xs text-muted-foreground italic">
                                  will skip
                                </span>
                              ) : (
                                <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={r.action === 'import'}
                                    onChange={(e) =>
                                      setRowDecisions((prev) => ({
                                        ...prev,
                                        [r.rowIndex]: e.target.checked ? 'import' : 'skip',
                                      }))
                                    }
                                  />
                                  Add
                                </label>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep('map')}>
                    <Icon name="ArrowLeft" size={14} />
                    Back
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleRunImport}
                    disabled={toImportCount === 0}
                    className="min-h-12"
                  >
                    <Icon name="Upload" size={14} />
                    Add {toImportCount} goal{toImportCount === 1 ? '' : 's'}
                  </Button>
                </div>
              </section>
            )}

            {/* STEP: IMPORT ------------------------------------------ */}
            {step === 'import' && (
              <section className="bg-card border border-border rounded-lg p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {isImporting ? 'Adding goals…' : 'Done'}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {isImporting
                      ? `Adding ${toImportCount} goals. Please leave this page open.`
                      : errorCount === 0
                        ? `Added ${successCount} goals to your students' records.`
                        : `Added ${successCount} goals. ${errorCount} row${errorCount === 1 ? '' : 's'} failed — see below.`}
                  </p>
                </div>

                <div className="rounded-md border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Student</th>
                        <th className="text-left px-3 py-2">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importLog.length === 0 && (
                        <tr>
                          <td colSpan={3} className="px-3 py-6 text-center text-muted-foreground text-sm">
                            <Icon name="Loader2" size={16} className="inline animate-spin mr-2" />
                            Starting…
                          </td>
                        </tr>
                      )}
                      {importLog.map((entry) => (
                        <tr key={entry.rowIndex} className="border-t border-border">
                          <td className="px-3 py-2 text-xs text-muted-foreground">
                            #{entry.rowIndex}
                          </td>
                          <td className="px-3 py-2 font-medium">{entry.name}</td>
                          <td className="px-3 py-2">
                            {entry.status === 'added' && (
                              <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                <Icon name="Check" size={12} />
                                Added
                              </span>
                            )}
                            {entry.status === 'error' && (
                              <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                <Icon name="AlertCircle" size={12} />
                                {entry.error || 'Error'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {!isImporting && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Button variant="outline" onClick={handleStartOver}>
                      <Icon name="RotateCcw" size={14} />
                      Import another spreadsheet
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => navigate('/iep-goals-and-objectives-tracking')}
                      className="min-h-12"
                    >
                      <Icon name="Target" size={14} />
                      See my goals
                    </Button>
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const SummaryTile = ({ label, count, tone }) => {
  const toneClass =
    tone === 'ok'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : tone === 'bad'
        ? 'border-red-200 bg-red-50 text-red-900'
        : 'border-amber-200 bg-amber-50 text-amber-900';
  return (
    <div className={`rounded-lg border p-3 ${toneClass}`}>
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
};

export default GoalsImportPage;
